import {
  type ParticipantRestrictivePractice,
  RestrictivePracticeType,
  type UpdateParticipantRestrictivePracticeRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { dateTimeSchema, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateParticipantRestrictivePracticeMutation } from '../../../graphql/hooks/useUpdateParticipantRestrictivePracticeMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantRestrictivePracticeForm } from './ParticipantRestrictivePracticeForm';

type FormValues = UpdateParticipantRestrictivePracticeRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  description: z.string().nonempty('Please enter a description'),
  type: z.enum(RestrictivePracticeType),
  identifiedAt: dateTimeSchema,
  startedAt: dateTimeSchema.optional().nullable(),
  ceasedAt: dateTimeSchema.optional().nullable(),
  labelIds: z.array(z.string()),
});

interface Props {
  participantRestrictivePractice: ParticipantRestrictivePractice;
  participantId: string;
}

export const ParticipantRestrictivePracticeEditModal = createModal<Props>((props) => {
  const { participantRestrictivePractice, participantId, modal } = props;

  const { hide, visible, remove } = modal;
  const { organization, successToast, errorToast } = useAppContext();
  const mutation = useUpdateParticipantRestrictivePracticeMutation();

  const initialValues: FormValues = {
    type: participantRestrictivePractice.type,
    description: participantRestrictivePractice.description,
    startedAt: participantRestrictivePractice.startedAt,
    identifiedAt: participantRestrictivePractice.identifiedAt,
    ceasedAt: participantRestrictivePractice.ceasedAt,
    labelIds: participantRestrictivePractice.labels.map((label) => label.id),
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantId,
          restrictivePracticeId: participantRestrictivePractice.id,
          request: values,
        })
        .then(async () => {
          hide();
          successToast('Restrictive practice updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating restrictive practice: ${err.message}`);
        });
    },
    [
      mutation,
      organization.id,
      participantId,
      participantRestrictivePractice.id,
      hide,
      successToast,
      errorToast,
    ],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit restrictive practice'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantRestrictivePracticeForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
